/** Shared scope-selection option. One pattern across every scope
 *  surface (ScopePicker cards, the Google configure step, the view
 *  selector primary row, the re-scope popover) so the user learns a
 *  single visual language: an explicit radio dot / check icon plus a
 *  strong selected border + tint + halo. Tasteful, not loud. No em
 *  dashes. */
import type { LucideIcon } from "lucide-react";
import { CheckCircle2, Circle } from "lucide-react";

export function ScopeOption({
  selected,
  disabled = false,
  onSelect,
  label,
  hint,
  Icon,
  compact = false,
  title,
  className = "",
}: {
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  label: string;
  hint?: string;
  Icon?: LucideIcon;
  /** Tight inline variant for primary toggle rows. */
  compact?: boolean;
  title?: string;
  className?: string;
}) {
  const base =
    "relative flex items-center gap-2 rounded-xl border-2 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900";
  const pad = compact ? "px-2.5 py-1.5" : "p-3";
  const state = disabled
    ? "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 text-slate-300 dark:text-slate-600 cursor-not-allowed"
    : selected
      ? "border-sky-500 bg-sky-500/15 ring-2 ring-sky-400/40 ring-offset-1 dark:ring-offset-slate-900 text-slate-900 dark:text-slate-100 cursor-pointer"
      : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500 cursor-pointer";
  const Indicator = selected ? CheckCircle2 : Circle;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      title={title}
      onClick={() => !disabled && onSelect()}
      className={`${base} ${pad} ${state} ${className}`}
    >
      <Indicator
        className={`shrink-0 h-5 w-5 ${
          selected
            ? "text-sky-600 dark:text-sky-400"
            : "text-slate-300 dark:text-slate-600"
        }`}
        strokeWidth={selected ? 2.5 : 2}
      />
      {Icon && (
        <Icon className="shrink-0 h-4 w-4 text-slate-500 dark:text-slate-400" />
      )}
      <span className="min-w-0 flex-1">
        <span
          className={`block capitalize ${
            compact ? "text-xs" : "text-sm"
          } ${selected ? "font-bold" : "font-medium"}`}
        >
          {label}
        </span>
        {hint && !compact && (
          <span className="block text-xs font-normal text-slate-500 dark:text-slate-400 mt-0.5 normal-case">
            {hint}
          </span>
        )}
      </span>
      {selected && !compact && (
        <span className="shrink-0 rounded-full bg-sky-600 text-white text-[10px] font-bold px-2 py-0.5">
          Selected
        </span>
      )}
    </button>
  );
}

export default ScopeOption;
